import StrickIcon from 'assets/img/icon-lightning-circle.svg';
import LessonIcon from 'assets/img/icon-lessons.svg';
import ClockIcon from 'assets/img/icon-clock.svg';

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Row, Col, Button } from 'antd';
import ScoringGetAchievementApi from './getAchievementApi';
import GetLeaderBoardApi from './getLeaderBoardApi';
import GetStateApi from './getStateApi';
import {
  CircularProgressbar,
  CircularProgressbarWithChildren,
  buildStyles
} from 'react-circular-progressbar';

import PentagonScore from 'components/charts/PentagonScore';
import 'react-circular-progressbar/dist/styles.css';
import './scoring.css';
import { useTranslation } from 'react-i18next';

function getListMonth() {
  const monthsOfYear = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const thisMonth = new Date().getMonth();
  return [...monthsOfYear.slice(thisMonth + 1), ...monthsOfYear.slice(0, thisMonth + 1)];
}

function getListDay() {
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().getDay();
  return [...daysOfWeek.slice(today + 1), ...daysOfWeek.slice(0, today + 1)];
}

const PostList: React.FC = () => {
  const { achievement } = ScoringGetAchievementApi();
  const { leaderBoard } = GetLeaderBoardApi();
  const { state } = GetStateApi();
  const { t } = useTranslation();

  const [streak, setStreak] = useState(0);

  const [yLesson7Days, setYLesson7Days] = useState<number[]>([]);
  const [yLesson12Months, setYLesson12Months] = useState<number[]>([]);
  const [yMinutes7Days, setYMinutes7Days] = useState<number[]>([]);
  const [yMinutes12Months, setYMinutes12Months] = useState<number[]>([]);

  const [maxLessonLast7Days, setMaxLessonLast7Days] = useState(0);
  const [maxLessonLast12Months, setMaxLessonLast12Months] = useState(0);
  const [maxMinutesLast7Days, setMaxMinutesLast7Days] = useState(0);
  const [maxMinutesLast12Months, setMaxMinutesLast12Months] = useState(0);

  const [isFilter7Days, setIsFilter7Days] = useState(true);
  const [isFilter12Month, setIsFilter12Month] = useState(false);

  const daysOfWeek = getListDay();
  const monthsOfYear = getListMonth();
  
  const levelOfScore = [
    {
      tag: 'Beginner',
      start_score: 0,
      end_score: 56
    },
    {
      tag: 'Lower Intermediate',
      start_score: 56,
      end_score: 66
    },
    {
      tag: 'Intermediate',
      start_score: 67,
      end_score: 77
    },
    {
      tag: 'Advanced',
      start_score: 78,
      end_score: 89
    },
    {
      tag: 'Native',
      start_score: 90,
      end_score: 100
    }
  ];
  enum IeltsBandLevel {
    didNotAttempt = 0,
    non = 1,
    intermittent = 2,
    extremelyLimited = 3,
    limited = 4,
    modest = 5,
    competent = 6,
    good = 7,
    veryGood = 8,
    expert = 9
  }
  const bandLevelTitle = (num: number) => {
    switch (num) {
      case IeltsBandLevel.didNotAttempt:
        return 'Did not attempt the test';
      case IeltsBandLevel.non:
        return 'Very Poor';
      case IeltsBandLevel.intermittent:
        return 'Intermittent';
      case IeltsBandLevel.extremelyLimited:
        return 'Extremely Limited';
      case IeltsBandLevel.limited:
        return 'Limited';
      case IeltsBandLevel.modest:
        return 'Modest';
      case IeltsBandLevel.competent:
        return 'Competent';
      case IeltsBandLevel.good:
        return 'Good';
      case IeltsBandLevel.veryGood:
        return 'Very Good';
      case IeltsBandLevel.expert:
        return 'Expert';
      default:
        break;
    }
  };
  React.useEffect(() => {
    setStreak(achievement?.data?.streak_day);
  }, [achievement]);
  const filter7Days = () => {
    setIsFilter7Days(true);
    setIsFilter12Month(false);
  };

  const filter12Month = () => {
    setIsFilter7Days(false);
    setIsFilter12Month(true);
  };

  const getMaxValue = (arr: Array<any>) => {
    return Math.max.apply(null, arr);
  };

  const roundMax = (number: number) => {
    if (number === 0) return 0;
    return number + (5 - (number % 5));
  };

  const generateAxisY = React.useCallback((dataSet) => {
    let axisY = [];
    const max = getMaxValue(dataSet);
    const roundedMax: number = roundMax(max);
    const divide = roundedMax / 5;
    if (roundedMax === 0) return [];
    for (let i = 1; i <= 5; i++) {
      axisY.push(divide * i);
    }
    return axisY;
  }, []);

  const isInRange = (num: number, start: number, end: number) => {
    return num >= start && num <= end;
  };

  React.useEffect(() => {
    const lessonsLearned = achievement?.data?.lessons_learned;
    const minutesSpent = achievement?.data?.minutes_spent;

    const lessonLast7Days = generateAxisY(lessonsLearned?.last_7_days.daily);
    const minutesLast7Days = generateAxisY(minutesSpent?.last_7_days.daily);
    const lessonLast12Months = generateAxisY(
      lessonsLearned?.last_12_months.monthly
    );
    const minutesLast12Months = generateAxisY(
      minutesSpent?.last_12_months.monthly
    );

    setYLesson7Days(lessonLast7Days);
    setYLesson12Months(lessonLast12Months);
    setYMinutes7Days(minutesLast7Days);
    setYMinutes12Months(minutesLast12Months);

    setMaxLessonLast7Days(
      roundMax(getMaxValue(lessonsLearned?.last_7_days.daily))
    );
    setMaxLessonLast12Months(
      roundMax(getMaxValue(lessonsLearned?.last_12_months.monthly))
    );
    setMaxMinutesLast7Days(
      roundMax(getMaxValue(minutesSpent?.last_7_days.daily))
    );
    setMaxMinutesLast12Months(
      roundMax(getMaxValue(minutesSpent?.last_12_months.monthly))
    );
  }, [achievement, generateAxisY]);

  return (
    <div className="score">
      <Helmet>
        <title>My Pronunciation Score - ELSA Speak</title>
        <meta
          name="description"
          content="Test my pronunciation level with ELSA Speak App for web PC. Redeem promo code Premium, Oxford Business Result, ELSA for Students, free account."
        />
      </Helmet>
      <h2 className="title">{t('score.your_score')}</h2>
      <section className="section-activity">
        <h3 className="section-title">{t('score.progress')}</h3>
        <div className="score-overview score-panel">
          <div className="overview-left">
            <div className="eps-score">
              {state ? (
                <CircularProgressbar
                  value={Math.round(state.data.eps) || 0}
                  text={`${Math.round(state.data.eps) || 0}%`}
                  styles={buildStyles({
                    pathColor: `rgba(28, 240, 219, 1)`,
                    textColor: '#FFFFFF',
                    trailColor: '#5B4BCF',
                    backgroundColor: '#3e98c7'
                  })}
                />
              ) : (
                <p className="chart-loading">{t('score.loading')}</p>
              )}
            </div>
            <div className="level">
              <p className="your-level">{t('score.your_level')}</p>
              {state ? (
                <div className="button-level">
                  {
                    levelOfScore.find((level) =>
                      isInRange(
                        state.data.eps,
                        level.start_score,
                        level.end_score
                      )
                    )?.tag
                  }
                </div>
              ) : (
                <div className="button-level">{t('score.loading')}</div>
              )}
            </div>
          </div>
          <div className="overview-right">
            <div className="pentagon-chart">
              {state ? (
                <PentagonScore
                  score={{
                    ons: state.data.ons,
                    lis: state.data.lis,
                    fls: state.data.fls,
                    wss: state.data.wss,
                    sis: state.data.sis
                  }}
                  chartSize={226}
                  rounded={2}
                />
              ) : (
                <p className="chart-loading">{t('score.loading')}</p>
              )}
            </div>
          </div>
          <div className="score-ielts">
            <h4>{t('score.predict_ielts')}</h4>
            <div className="score-dometer">
              {state ? (
                <CircularProgressbarWithChildren
                  className="score-dometer__path"
                  value={state?.data?.ielts * 10}
                  circleRatio={state?.data?.ielts / 10}
                  styles={buildStyles({
                    rotation: 1 / 2 + 1 / 6,
                    trailColor: '#453C9C',
                    pathColor: '#fff',
                    textColor: '#fff',
                    textSize: '28px'
                  })}
                >
                  <div className="value">{state?.data?.ielts}</div>
                  <div className="level">
                    {bandLevelTitle(Math.round(state?.data?.ielts))}
                  </div>
                </CircularProgressbarWithChildren>
              ) : (
                <p className="chart-loading">Đang tải</p>
              )}
            </div>
            <div className="authorization">
              IELTS Speaking Score Predictor
              <small>
                This score has not been approved or endorsed by IELTS or IDP
              </small>
            </div>
          </div>
        </div>
      </section>

      <section className="section-achievement">
        <h3 className="section-title">{t('score.achievements')}</h3>
        <div className="achievement-activity score-panel">
          <div className="title-container">
            <h4 className="subheading">{t('score.activity')}</h4>
            <p>{t('score.keep_track')}</p>
          </div>
          <Row className="chart-switching">
            <Col span={12}>
              <Button
                className={
                  'activity-log-btn' + (isFilter7Days ? ' active-btn' : '')
                }
                onClick={filter7Days}
              >
                {t('score.last_7_days')}
              </Button>
            </Col>
            <Col span={12}>
              <Button
                className={
                  'activity-log-btn' + (isFilter12Month ? ' active-btn' : '')
                }
                onClick={filter12Month}
              >
               {t('score.last_12_months')}
              </Button>
            </Col>
          </Row>

          <div className="chart-wrapper">
            {isFilter7Days &&
              (achievement ? (
                <>
                  <div className="total-counter">
                    <div className="chart-label chart-label--lesson">
                      <span className="chart-label__number">
                        <span className="chart-label__icon">
                          <img src={LessonIcon} alt="" />
                        </span>
                        <strong>
                          {achievement.data.lessons_learned.last_7_days.total ||
                            0}
                        </strong>
                        <small>{t('score.lessons_completed')}</small>
                      </span>
                    </div>
                    <div className="chart-label chart-label--minutes">
                      <span className="chart-label__number">
                        <span className="chart-label__icon">
                          <img src={ClockIcon} alt="" />
                        </span>
                        <strong>
                          {achievement.data.minutes_spent.last_7_days.total ||
                            0}
                        </strong>
                        <small> {t('score.minutes_spent')}</small>
                      </span>
                    </div>
                  </div>
                  <Row gutter={30} justify="space-between">
                    <Col xs={24} xl={12}>
                      <div className="achievement-chart chart-lesson chart-lesson--7">
                        <div className="chart-yAxis">
                          {yLesson7Days.map((item, index) => (
                            <div className="item" key={index}>
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                        <div className="chart-column">
                          {achievement?.data?.lessons_learned.last_7_days.daily.map(
                            (column: any, index: number) => (
                              <div key={index} className="column">
                                <div
                                  className="bar"
                                  style={{
                                    height:
                                      (column / maxLessonLast7Days) * 100 + '%'
                                  }}
                                ></div>
                              </div>
                            )
                          )}
                        </div>
                        <div className="chart-xAxis">
                          {daysOfWeek.map((day, index) => (
                            <div className="item" key={index}>
                              {day}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} xl={12}>
                      <div className="achievement-chart chart-minutes chart-minutes--7">
                        <div className="chart-yAxis">
                          {yMinutes7Days.map((item, index) => (
                            <div className="item" key={index}>
                              <span>{item} m</span>
                            </div>
                          ))}
                        </div>
                        <div className="chart-column">
                          {achievement?.data?.minutes_spent.last_7_days.daily.map(
                            (column: any, index: number) => (
                              <div key={index} className="column">
                                <div
                                  className="bar"
                                  style={{
                                    height:
                                      (column / maxMinutesLast7Days) * 100 + '%'
                                  }}
                                ></div>
                              </div>
                            )
                          )}
                        </div>
                        <div className="chart-xAxis">
                          {daysOfWeek.map((day, index) => (
                            <div className="item" key={index}>
                              {day}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </>
              ) : (
                <p className="chart-loading">{t('score.loading')}</p>
              ))}

            {isFilter12Month &&
              (achievement ? (
                <>
                  <div className="total-counter">
                    <div className="chart-label chart-label--lesson">
                      <span className="chart-label__number">
                        <span className="chart-label__icon">
                          <img src={LessonIcon} alt="" />
                        </span>
                        <strong>
                          {achievement.data.lessons_learned.last_12_months
                            .total || 0}
                        </strong>
                        <small>{t('score.lessons_completed')}</small>
                      </span>
                    </div>
                    <div className="chart-label chart-label--minutes">
                      <span className="chart-label__number">
                        <span className="chart-label__icon">
                          <img src={ClockIcon} alt="" />
                        </span>
                        <strong>
                          {achievement.data.minutes_spent.last_12_months
                            .total || 0}
                        </strong>
                        <small>{t('score.minutes_spent')}</small>
                      </span>
                    </div>
                  </div>
                  <Row gutter={30}>
                    <Col xs={24} xl={12}>
                      <div className="achievement-chart chart-lesson chart-lesson--12">
                        <div className="chart-yAxis">
                          {yLesson12Months.map((item, index) => (
                            <div className="item" key={index}>
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                        <div className="chart-column">
                          {achievement?.data?.lessons_learned.last_12_months.monthly.map(
                            (column: any, index: number) => (
                              <div key={index} className="column">
                                <div
                                  className="bar"
                                  style={{
                                    height:
                                      (column / maxLessonLast12Months) * 100 +
                                      '%'
                                  }}
                                ></div>
                              </div>
                            )
                          )}
                        </div>
                        <div className="chart-xAxis">
                          {monthsOfYear.map((day, index) => (
                            <div className="item" key={index}>
                              {day}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                    <Col xs={24} xl={12}>
                      <div className="achievement-chart chart-minutes chart-minutes--12">
                        <div className="chart-yAxis">
                          {yMinutes12Months.map((item, index) => (
                            <div className="item" key={index}>
                              <span>{item} m</span>
                            </div>
                          ))}
                        </div>
                        <div className="chart-column">
                          {achievement?.data?.minutes_spent.last_12_months.monthly.map(
                            (column: any, index: number) => (
                              <div key={index} className="column">
                                <div
                                  className="bar"
                                  style={{
                                    height:
                                      (column / maxMinutesLast12Months) * 100 +
                                      '%'
                                  }}
                                ></div>
                              </div>
                            )
                          )}
                        </div>
                        <div className="chart-xAxis">
                          {monthsOfYear.map((day, index) => (
                            <div className="item" key={index}>
                              {day}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </>
              ) : (
                <p className="chart-loading">{t('score.loading')}...</p>
              ))}
          </div>
        </div>
      </section>

      <section className="section-result">
        <Row justify="space-between">
          <div className="score-panel ant-col">
            <div className="title-container">
              <h4 className="subheading">{t('score.current_streak')}</h4>
              <p dangerouslySetInnerHTML={{__html: t('score.complete_one_lesson')}}></p>
            </div>
            <div className="box-result">
              <div className="result-detail result-detail--strick">
                <span className="total">{streak}</span>
              </div>
              {achievement && (
                <ul className="day-strick-progress">
                  {achievement.data.lessons_learned.last_7_days.daily.map(
                    (day: any, index: number) => (
                      <li key={index} className={!day ? 'disabled' : ''}>
                        <img src={StrickIcon} alt="Strick" />
                      </li>
                    )
                  )}
                </ul>
              )}
            </div>
          </div>
          <div className="score-panel ant-col">
            <div className="title-container">
              <h4 className="subheading">{t('score.achievements')}</h4>
              <p>{t('score.earn_more_stars')}</p>
            </div>
            <div className="box-result">
              {leaderBoard && (
                <div className="result-detail result-detail--achievement">
                  <span className="total">{ leaderBoard.data.points || 0 }
                  </span>
                  <p className="accumulation">{t('score.accumulated')}</p>
                </div>
              )}
            </div>
          </div>
        </Row>
      </section>
    </div>
  );
};

export default PostList;
